import { Solution } from "./types";

export const solutions: Solution[] = [
  {
    slug: "healthcare",
    title: "Healthcare",
    icon: "heart",
    headline: "Extend support beyond the appointment.",
    description:
      "Help people put agreed goals into practice in the days between professional conversations. Explore adaptive support that complements your care.",
    audience:
      "For clinics, psychologists, dietitians, coaches, and digital care teams.",
    problem: "An hour together. A whole week of everyday life.",
    needs: [
      [
        "Good intentions meet real barriers",
        "People leave with a plan. Busy days, low energy, and uncertainty can make following through difficult.",
      ],
      [
        "Time together is limited",
        "Routine questions and catching up can use time that professionals need for the person in front of them.",
      ],
      [
        "Everyday context gets lost",
        "What was difficult on Tuesday may be hard to recall by the next appointment.",
      ],
    ],
    steps: [
      [
        "Agree the scope",
        "Choose one support need and define what remains the responsibility of the professional.",
      ],
      [
        "Shape the experience",
        "Agree the check-ins, messaging, reflections, and any human follow-up rules appropriate for your service.",
      ],
      [
        "Evaluate together",
        "Plan consent, feedback, safety review, and useful measures before expanding a pilot.",
      ],
    ],
    cases: [
      "between-session-support",
      "weight-management",
      "chronic-condition-management",
    ],
    boundary:
      "Organizational workflows are scoped with each partner. Clinical-system integrations, automated escalation, and care outcomes are not presented as established capabilities.",
  },
  {
    slug: "employers",
    title: "Employers",
    icon: "people",
    headline: "Support people through the everyday parts of work.",
    description:
      "Explore private, practical support for focus, stress, recovery, and sustainable routines. Built around people, not productivity scores.",
    audience:
      "For people teams, wellbeing leads, and organizations supporting diverse ways of working.",
    problem: "Wellbeing is shaped by the ordinary working day.",
    needs: [
      [
        "A demanding day does not end at work",
        "Stress can follow people home. Recovery and everyday routines deserve attention too.",
      ],
      [
        "Different people need different support",
        "Communication style, executive functioning, and sensory needs vary from person to person.",
      ],
      [
        "Trust makes participation possible",
        "People need a clear understanding of privacy and choice before engaging with a workplace program.",
      ],
    ],
    steps: [
      [
        "Start with a real need",
        "Choose a practical focus such as recovery, transitions, or everyday planning.",
      ],
      [
        "Make participation a choice",
        "Set clear information, appropriate access, and boundaries around the employee experience.",
      ],
      [
        "Review without surveillance",
        "Discuss program feedback only in appropriately aggregated, privacy-preserving forms. Private conversations are not an employer dashboard.",
      ],
    ],
    cases: ["stress-anxiety", "sleep", "neurodivergent-support", "adhd"],
    boundary:
      "A proposed employer program must define data access and consent explicitly. It should never be used for individual employee monitoring, performance scoring, or diagnosis.",
  },
  {
    slug: "education",
    title: "Education",
    icon: "book",
    headline:
      "Help students stay on track when nobody is standing next to them.",
    description:
      "Explore everyday support for study planning, deadlines, routines, and the transition into university life.",
    audience:
      "For universities, colleges, student wellbeing teams, disability support, and student success programs.",
    problem: "Support services cannot be beside every student, every day.",
    needs: [
      [
        "Freedom comes with invisible work",
        "Planning, prioritizing, and managing deadlines are skills students may still be building.",
      ],
      [
        "The difficult moment happens later",
        "A useful conversation with an adviser still has to turn into action in a library or bedroom.",
      ],
      [
        "One approach will not fit everyone",
        "Neurodivergent students and those navigating new routines need room for individual preferences.",
      ],
    ],
    steps: [
      [
        "Choose the student journey",
        "Focus on a defined transition, study challenge, or support program.",
      ],
      [
        "Connect to your team",
        "Clarify the role of tutors, accessibility services, and wellbeing professionals.",
      ],
      [
        "Learn from a scoped pilot",
        "Agree participation, accessibility, feedback, and follow-up before offering broader access.",
      ],
    ],
    cases: ["student-support", "adhd", "stress-anxiety", "sleep"],
    boundary:
      "Student support complements human services and accommodations. There are no claimed university deployments or verified attainment outcomes on this site.",
  },
  {
    slug: "digital-health",
    title: "Digital Health Partners",
    icon: "link",
    headline: "Bring everyday support into the experience you already offer.",
    description:
      "Explore an adaptive conversational layer for your service, connecting useful context with timely, practical support.",
    audience:
      "For digital health platforms, care-program developers, and product teams.",
    problem: "Another destination is not always what people need.",
    needs: [
      [
        "Engagement happens between logins",
        "A contextual check-in can help an intention return at a useful time.",
      ],
      [
        "Context needs a purpose",
        "A goal or reflection only becomes useful when it changes the support someone receives.",
      ],
      [
        "Integration is also a service decision",
        "Consent, ownership, responsibilities, and support boundaries matter alongside technical connections.",
      ],
    ],
    steps: [
      [
        "Map the experience",
        "Define the user journey, helpful context, communication channels, and success criteria.",
      ],
      [
        "Review the connection",
        "Discuss API requirements, access controls, data flows, and what is feasible with the current platform.",
      ],
      [
        "Prove a narrow workflow",
        "Build and evaluate a scoped prototype before making a production commitment.",
      ],
    ],
    cases: [
      "between-session-support",
      "chronic-condition-management",
      "weight-management",
    ],
    boundary:
      "APIs, wearable connections, and clinical-system integrations require a feasibility discussion. No public API, named device partnership, or production EHR connection is claimed.",
  },
];
